/**
 * @jsxRuntime classic
 * @jsx jsx
 */

import { useCallback, useEffect, useMemo, useRef, useState, Fragment } from 'react';

import { useIntl } from 'react-intl';

import Button, { IconButton } from '@atlaskit/button/new';
import { cssMap, cx, jsx } from '@atlaskit/css';
import DeleteIcon from '@atlaskit/icon/core/delete';
import SearchIcon from '@atlaskit/icon/core/search';
import Image from '@atlaskit/image';
import { useResizingHeight } from '@atlaskit/motion';
import { Box, Stack, Text } from '@atlaskit/primitives/compiled';
import Spinner from '@atlaskit/spinner';
import Textfield from '@atlaskit/textfield';
import { token } from '@atlaskit/tokens';
import Tooltip from '@atlaskit/tooltip';
import { useAnalytics } from '@atlassian/conversation-assistant-instrumentation/controllers/analytics';

import memoryDisabledIllustrationDark from './assets/memory-disabled-illustration-dark.svg';
import memoryDisabledIllustration from './assets/memory-disabled-illustration.svg';
import memoryEmptyIllustrationDark from './assets/memory-empty-illustration-dark.svg';
import memoryEmptyIllustration from './assets/memory-empty-illustration.svg';
import memoryListErrorIllustration from './assets/memory-list-error.svg';
import { messages } from './messages';

export type MemoryItem = {
	id: string;
	content: string;
};

const styles = cssMap({
	container: {
		display: 'flex',
		flexDirection: 'column',
		width: '100%',
		height: '100%',
		border: `${token('border.width')} solid ${token('color.border')}`,
		borderRadius: token('radius.xlarge'),
		overflow: 'hidden',
	},
	containerDisabled: {
		opacity: token('opacity.disabled'),
		pointerEvents: 'none',
	},
	disabledWrapper: {
		position: 'relative',
		width: '100%',
		minHeight: '200px',
	},
	disabledOverlay: {
		position: 'absolute',
		inset: 0,
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
		zIndex: 500,
	},
	disabledOverlayCard: {
		display: 'flex',
		flexDirection: 'column',
		alignItems: 'center',
		justifyContent: 'center',
		textAlign: 'center',
		gap: token('space.150'),
		paddingBlock: token('space.400'),
		paddingInline: token('space.300'),
		width: '100%',
		maxWidth: '430px',
		backgroundColor: token('elevation.surface'),
		borderRadius: token('radius.medium'),
		boxShadow: token('elevation.shadow.overflow'),
	},
	list: {
		overflowY: 'auto',
		overflowX: 'hidden',
		scrollbarWidth: 'thin',
		scrollbarGutter: 'stable',
		flex: '1 1 0%',
		minHeight: '0px',
		// maxHeight: "320px",
	},
	listWithExtraBottomPadding: {
		paddingBottom: token('space.1000'),
	},
	rowWrapper: {
		borderTop: `${token('border.width')} solid ${token('color.border')}`,
		boxSizing: 'border-box',
		position: 'relative',
	},
	rowWrapperFirst: {
		borderTop: 'none',
		boxSizing: 'border-box',
		position: 'relative',
		borderTopLeftRadius: token('radius.xlarge'),
		borderTopRightRadius: token('radius.xlarge'),
	},
	rowWrapperLast: {
		borderBottom: `${token('border.width')} solid ${token('color.border')}`,
		boxSizing: 'border-box',
		position: 'relative',
	},
	rowWrapperExpanding: {
		zIndex: 1,
		backgroundColor: token('elevation.surface'),
	},
	row: {
		display: 'flex',
		flexDirection: 'row',
		alignItems: 'center',
		justifyContent: 'space-between',
		gap: token('space.150'),
		width: '100%',
		paddingBlock: token('space.100'),
		paddingInlineStart: token('space.200'),
		paddingInlineEnd: token('space.150'),
	},
	rowContent: {
		display: 'flex',
		paddingBlock: token('space.150'),
		alignItems: 'flex-start',
		gap: token('space.150'),
		alignSelf: 'stretch',
		flex: '1 1 0%',
		minWidth: '0px',
	},
	rowContentText: {
		overflowWrap: 'break-word',
		minWidth: '0px',
	},
	rowActions: {
		flexShrink: 0,
	},
	emptyState: {
		display: 'flex',
		flexDirection: 'column',
		alignItems: 'center',
		justifyContent: 'center',
		flex: '1 1 0%',
		minHeight: '200px',
		paddingBlock: token('space.400'),
		paddingInline: token('space.300'),
		textAlign: 'center',
	},
	emptyStateHeading: {
		marginBlockEnd: token('space.100'),
		color: token('color.text'),
	},
	emptyStateDescription: {
		color: token('color.text.subtle'),
		maxWidth: '400px',
	},
	illustrationWrapper: {
		marginBlockEnd: token('space.150'),
	},
	deleteConfirmRow: {
		display: 'flex',
		flexDirection: 'column',
		width: '100%',
		gap: token('space.150'),
		paddingTop: token('space.150'),
		paddingRight: token('space.150'),
		paddingBottom: token('space.150'),
		paddingLeft: token('space.150'),
		backgroundColor: token('elevation.surface.sunken'),
	},
	deleteConfirmRowFirst: {
		backgroundColor: token('elevation.surface.sunken'),
		borderTopRightRadius: token('radius.xlarge'),
		borderTopLeftRadius: token('radius.xlarge'),
	},
	deleteConfirmRowLast: {
		borderBottomRightRadius: token('radius.xlarge'),
		borderBottomLeftRadius: token('radius.xlarge'),
	},
	memoryPreview: {
		backgroundColor: token('elevation.surface'),
		borderRadius: token('radius.medium'),
		boxShadow: token('elevation.shadow.overflow'),
		paddingBlock: token('space.100'),
		paddingInline: token('space.150'),
	},
	deleteConfirmContent: {
		display: 'flex',
		flexDirection: 'row',
		alignItems: 'flex-start',
		justifyContent: 'space-between',
		gap: token('space.150'),
		flexWrap: 'nowrap',
	},
	deleteConfirmTextWrapper: {
		display: 'flex',
		flexDirection: 'column',
		gap: token('space.050'),
		minWidth: '0px',
		flex: '1 1 auto',
		paddingInlineStart: token('space.100'),
	},
	deleteConfirmActions: {
		display: 'flex',
		flexDirection: 'row',
		gap: token('space.100'),
		flexShrink: 0,
	},
	errorStateContent: {
		maxWidth: '430px',
	},
	searchContainer: {
		flexShrink: 0,
		paddingBottom: token('space.150'),
	},
	searchIconContainer: {
		paddingInlineStart: token('space.100'),
	},
});

export type MemoryListProps = {
	memories?: MemoryItem[];
	isDisabled?: boolean;
	isError?: boolean;
	onDelete: (memoryId: string) => Promise<void>;
	/** Optional initial memory id to show delete confirmation for (e.g. for Storybook) */
	initialMemoryIdConfirming?: string | null;
	isLoading?: boolean;
	onRetry?: () => Promise<void>;
	/** Called when a memory is successfully deleted */
	onDeleteSuccess?: () => void;
	/** Called when a memory deletion fails */
	onDeleteError?: () => void;
	/** Whether to show a search bar above the list */
	isSearchable?: boolean;
	/** Adds extra bottom padding inside the scroll area, e.g. to leave space for a floating button */
	hasExtraBottomPadding?: boolean;
};

const EMPTY_ILLUSTRATION = {
	src: memoryEmptyIllustration,
	srcDark: memoryEmptyIllustrationDark,
	width: 73,
	height: 52,
} as const;

const DISABLED_ILLUSTRATION = {
	src: memoryDisabledIllustration,
	srcDark: memoryDisabledIllustrationDark,
	width: 60,
	height: 39,
} as const;

type EmptyStateContentProps = {
	illustration: { src: string; srcDark?: string; width: number; height: number };
	heading: string;
	description: string;
};

const EmptyStateContent = ({ illustration, heading, description }: EmptyStateContentProps) => (
	<Stack space="space.0">
		<Box xcss={styles.illustrationWrapper}>
			<Image
				src={illustration.src}
				srcDark={illustration.srcDark}
				alt=""
				width={illustration.width}
				height={illustration.height}
			/>
		</Box>
		<Box xcss={styles.emptyStateHeading}>
			<Text size="large" weight="semibold" as="p">
				{heading}
			</Text>
		</Box>
		<Box xcss={styles.emptyStateDescription}>
			<Text size="medium" as="p">
				{description}
			</Text>
		</Box>
	</Stack>
);

const MemoryListDisabledOverlay = () => {
	const { formatMessage } = useIntl();
	return (
		<Box xcss={styles.disabledOverlayCard}>
			<Stack>
				<Box xcss={styles.illustrationWrapper}>
					<Image
						src={DISABLED_ILLUSTRATION.src}
						srcDark={DISABLED_ILLUSTRATION.srcDark}
						alt=""
						width={DISABLED_ILLUSTRATION.width}
						height={DISABLED_ILLUSTRATION.height}
					/>
				</Box>
				<Stack space="space.050">
					<Text weight="semibold" as="p">
						{formatMessage(messages.disabledHeading)}
					</Text>
					<Text color="color.text.subtle" as="p">
						{formatMessage(messages.disabledDescription)}
					</Text>
				</Stack>
			</Stack>
		</Box>
	);
};

type MemoryListErrorStateProps = {
	onRetry?: () => Promise<void>;
	isLoading?: boolean;
};

const MemoryListErrorState = ({ onRetry, isLoading = false }: MemoryListErrorStateProps) => {
	const { formatMessage } = useIntl();

	return (
		<Stack xcss={styles.container}>
			<Box xcss={styles.emptyState}>
				<Stack alignInline="center" space="space.250" xcss={styles.errorStateContent}>
					<Stack alignInline="center" space="space.050">
						<Image src={memoryListErrorIllustration} alt="" width={88} height={88} />
						<Text color="color.text.subtle" size="medium" weight="bold">
							{formatMessage(messages.errorHeading)}
						</Text>
						<Text color="color.text.subtle" size="medium">
							{formatMessage(messages.errorDescription)}
						</Text>
					</Stack>
					<Button onClick={onRetry} isDisabled={isLoading}>
						{formatMessage(messages.errorRetry)}
					</Button>
				</Stack>
			</Box>
		</Stack>
	);
};

const MemoryListLoadingState = () => {
	const { formatMessage } = useIntl();

	return (
		<Stack xcss={styles.container} space="space.0">
			<Box xcss={styles.emptyState}>
				<Stack alignInline="center" space="space.150">
					<Spinner />
					<Text>{formatMessage(messages.loadingDescription)}</Text>
				</Stack>
			</Box>
		</Stack>
	);
};

type MemoryDeleteConfirmProps = {
	memory: MemoryItem;
	onCancel: () => void;
	onRemove: () => void;
	isFirst: boolean;
	isLast: boolean;
	isLoading?: boolean;
};

const MemoryDeleteConfirm = ({
	memory,
	onCancel,
	onRemove,
	isFirst,
	isLast,
	isLoading = false,
}: MemoryDeleteConfirmProps) => {
	const { formatMessage } = useIntl();
	return (
		<Box
			xcss={cx(
				styles.deleteConfirmRow,
				isFirst && styles.deleteConfirmRowFirst,
				isLast && styles.deleteConfirmRowLast,
			)}
		>
			<Box xcss={styles.memoryPreview}>
				<Text>{memory.content}</Text>
			</Box>
			<Box xcss={styles.deleteConfirmContent}>
				<Box xcss={styles.deleteConfirmTextWrapper}>
					<Text weight="bold">{formatMessage(messages.deleteConfirmHeading)}</Text>
					<Text size="small" color="color.text.subtlest">
						{formatMessage(messages.deleteConfirmDescription)}
					</Text>
				</Box>
				<Box xcss={styles.deleteConfirmActions}>
					<Button appearance="subtle" onClick={onCancel} isDisabled={isLoading}>
						{formatMessage(messages.deleteConfirmCancel)}
					</Button>
					<Button
						appearance="warning"
						onClick={onRemove}
						isLoading={isLoading}
						isDisabled={isLoading}
					>
						{formatMessage(messages.deleteConfirmRemove)}
					</Button>
				</Box>
			</Box>
		</Box>
	);
};

const MemoryListEmptyContent = () => {
	const { formatMessage } = useIntl();
	return (
		<Box xcss={styles.emptyState}>
			<EmptyStateContent
				illustration={EMPTY_ILLUSTRATION}
				heading={formatMessage(messages.emptyListHeading)}
				description={formatMessage(messages.emptyListDescription)}
			/>
		</Box>
	);
};

const MemoryListEmptyState = () => {
	return (
		<Stack xcss={styles.container} space="space.0">
			<MemoryListEmptyContent />
		</Stack>
	);
};

const MemoryList = ({
	memories = [],
	isDisabled = false,
	isError = false,
	onDelete,
	initialMemoryIdConfirming = null,
	isLoading = false,
	onRetry,
	onDeleteSuccess,
	onDeleteError,
	isSearchable = true,
	hasExtraBottomPadding = false,
}: MemoryListProps): JSX.Element => {
	const [memoryIdConfirming, setMemoryIdConfirming] = useState<string | null>(
		initialMemoryIdConfirming ?? null,
	);
	const [memoryIdDeleting, setMemoryIdDeleting] = useState<string | null>(null);
	const [searchQuery, setSearchQuery] = useState('');
	const { sendAnalyticsEvent } = useAnalytics();
	const { formatMessage } = useIntl();

	const filteredMemories = useMemo(() => {
		const regex = new RegExp(searchQuery, 'i');
		if (!isSearchable || !regex) {
			return memories;
		}
		return memories.filter((m) => regex.test(m.content));
	}, [memories, searchQuery, isSearchable]);

	const sendMemoryAnalytics = useCallback(
		(actionSubjectId: string, memoryId: string) => {
			sendAnalyticsEvent({
				action: 'clicked',
				actionSubject: 'button',
				actionSubjectId,
				attributes: { memoryId },
			});
		},
		[sendAnalyticsEvent],
	);

	const handleDeleteClick = useCallback(
		(memoryId: string) => {
			sendMemoryAnalytics('memoryDeleteButton', memoryId);
			setMemoryIdConfirming(memoryId);
		},
		[sendMemoryAnalytics],
	);

	const handleCancelConfirm = useCallback(
		(memoryId: string) => {
			sendMemoryAnalytics('memoryDeleteCancelButton', memoryId);
			setMemoryIdConfirming(null);
		},
		[sendMemoryAnalytics],
	);

	const handleConfirmRemove = useCallback(
		async (memoryId: string) => {
			sendMemoryAnalytics('memoryDeleteConfirmButton', memoryId);
			setMemoryIdDeleting(memoryId);
			try {
				await Promise.resolve(onDelete(memoryId));
				setMemoryIdConfirming(null);
				onDeleteSuccess?.();
			} catch {
				onDeleteError?.();
			} finally {
				setMemoryIdDeleting(null);
			}
		},
		[onDelete, onDeleteSuccess, onDeleteError, sendMemoryAnalytics],
	);

	if (isError) {
		return <MemoryListErrorState onRetry={onRetry} isLoading={isLoading} />;
	}

	if (isLoading) {
		return <MemoryListLoadingState />;
	}

	if (filteredMemories.length === 0 && !isDisabled) {
		return (
			<Fragment>
				{isSearchable && (
					<Box xcss={styles.searchContainer}>
						<Textfield
							value={searchQuery}
							onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSearchQuery(e.target.value)}
							placeholder={formatMessage(messages.searchPlaceholder)}
							aria-label={formatMessage(messages.searchLabel)}
							elemBeforeInput={
								<Box xcss={styles.searchIconContainer}>
									<SearchIcon label="" color={token('color.icon.subtle')} />
								</Box>
							}
							testId="memory-list-search"
						/>
					</Box>
				)}
				<MemoryListEmptyState />
			</Fragment>
		);
	}

	// When disabled: show list (or empty state) with overlay on top. When not disabled: show list with rows.
	const memoryListContent = (
		<Fragment>
			{isSearchable && (
				<Box xcss={styles.searchContainer}>
					<Textfield
						value={searchQuery}
						onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSearchQuery(e.target.value)}
						placeholder={formatMessage(messages.searchPlaceholder)}
						aria-label={formatMessage(messages.searchLabel)}
						elemBeforeInput={
							<Box xcss={styles.searchIconContainer}>
								<SearchIcon label="" color={token('color.icon.subtle')} />
							</Box>
						}
						testId="memory-list-search"
					/>
				</Box>
			)}
			<Stack xcss={cx(styles.container, isDisabled && styles.containerDisabled)} space="space.0">
				{filteredMemories.length === 0 ? (
					<Box xcss={styles.emptyState}>
						<EmptyStateContent
							illustration={EMPTY_ILLUSTRATION}
							heading={formatMessage(messages.emptyListHeading)}
							description={formatMessage(messages.emptyListDescription)}
						/>
					</Box>
				) : (
					<Fragment>
						<Box xcss={cx(styles.list, hasExtraBottomPadding && styles.listWithExtraBottomPadding)}>
							{filteredMemories.map((memory, index) => (
								<MemoryRow
									key={memory.id}
									memory={memory}
									isFirst={index === 0}
									isLast={index === filteredMemories.length - 1}
									isDisabled={isDisabled}
									confirmingId={memoryIdConfirming}
									isDeleting={memoryIdDeleting === memory.id}
									onDeleteClick={() => handleDeleteClick(memory.id)}
									onCancelConfirm={() => handleCancelConfirm(memory.id)}
									onConfirmRemove={() => handleConfirmRemove(memory.id)}
								/>
							))}
						</Box>
					</Fragment>
				)}
			</Stack>
		</Fragment>
	);

	if (isDisabled) {
		return (
			<Box xcss={styles.disabledWrapper}>
				{memoryListContent}
				<Box xcss={styles.disabledOverlay}>
					<MemoryListDisabledOverlay />
				</Box>
			</Box>
		);
	}

	return memoryListContent;
};

type MemoryRowProps = {
	memory: MemoryItem;
	isFirst?: boolean;
	isDisabled?: boolean;
	confirmingId: string | null;
	isDeleting?: boolean;
	onDeleteClick: () => void;
	onCancelConfirm: () => void;
	onConfirmRemove: () => void | Promise<void>;
	isLast?: boolean;
};

const MemoryRow = ({
	memory,
	isFirst,
	isDisabled,
	confirmingId,
	isDeleting = false,
	onDeleteClick,
	onCancelConfirm,
	onConfirmRemove,
	isLast,
}: MemoryRowProps) => {
	const { formatMessage } = useIntl();
	const isConfirming = confirmingId === memory.id;
	const resizing = useResizingHeight();
	const rowRef = useRef<HTMLDivElement | null>(null);

	useEffect(() => {
		if (isConfirming && rowRef.current) {
			// Wait for the resizing animation to start before scrolling into view
			requestAnimationFrame(() => {
				rowRef.current?.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
			});
		}
	}, [isConfirming]);

	return (
		<Box
			ref={rowRef}
			xcss={cx(
				isFirst ? styles.rowWrapperFirst : styles.rowWrapper,
				isLast && styles.rowWrapperLast,
				isConfirming && styles.rowWrapperExpanding,
			)}
		>
			<div {...resizing}>
				{isConfirming ? (
					<MemoryDeleteConfirm
						memory={memory}
						onCancel={onCancelConfirm}
						onRemove={onConfirmRemove}
						isFirst={isFirst ?? false}
						isLast={isLast ?? false}
						isLoading={isDeleting}
					/>
				) : (
					<Box xcss={styles.row} as="div">
						<Box xcss={styles.rowContent}>
							<Box xcss={styles.rowContentText}>
								<Text>{memory.content}</Text>
							</Box>
						</Box>
						<Box xcss={styles.rowActions}>
							<Tooltip content={formatMessage(messages.deleteMemoryTooltip)}>
								{(tooltipProps) => (
									<IconButton
										{...tooltipProps}
										icon={DeleteIcon}
										label={formatMessage(messages.deleteMemory)}
										appearance="subtle"
										isDisabled={isDisabled}
										onClick={onDeleteClick}
									/>
								)}
							</Tooltip>
						</Box>
					</Box>
				)}
			</div>
		</Box>
	);
};

export default MemoryList;
